import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import WhatShouldAvoidWhenPlanning from '$/_const/dna/WhatShouldAvoidWhenPlanning.ts';
import type { WhatShouldAvoidWhenPlanning as AvoidanceCategory } from '$/_const/dna/WhatShouldAvoidWhenPlanning.ts';
import { savedCategories } from '@/utils/dna/avoidance.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';

import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaPill from '@/components/functional/TravelDnaManipulationForm/shared/DnaPill.tsx';
import DnaPrimaryButton from '@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

const yourDefaultsSchema = dnaSchema.pick({
  what_should_avoid_when_planning: true,
  what_should_avoid_when_planning_details: true,
  what_should_avoid_when_planning_custom: true,
});

export type YourDefaultsSchema = z.infer<typeof yourDefaultsSchema>;

type YourDefaultsProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const visibleCategories: AvoidanceCategory[] = [
  WhatShouldAvoidWhenPlanning.PACE_AND_TIMING,
  WhatShouldAvoidWhenPlanning.CROWDS,
  WhatShouldAvoidWhenPlanning.CLIMATE_AND_SURROUNDINGS,
  WhatShouldAvoidWhenPlanning.FOOD_AND_DRINK,
  WhatShouldAvoidWhenPlanning.WHERE_YOU_STAY,
  WhatShouldAvoidWhenPlanning.ACTIVITIES,
  WhatShouldAvoidWhenPlanning.TRANSPORT,
  WhatShouldAvoidWhenPlanning.EVENTS_AND_ENTERTAINMENT,
];

const categoryDetails: Partial<
  Record<AvoidanceCategory, readonly string[]>
> = {
  [WhatShouldAvoidWhenPlanning.PACE_AND_TIMING]: [
    'early_mornings',
    'late_nights',
    'packed_days',
    'long_walks',
    'lots_of_steps',
    'steep_hills',
    'standing_for_long_periods',
    'long_travel_days',
    'tight_connections',
    'multiple_hotel_changes',
    'red_eye_flights',
  ],

  [WhatShouldAvoidWhenPlanning.CROWDS]: [
    'crowds',
    'queues',
    'tourist_traps',
    'rush_hour_travel',
  ],

  [WhatShouldAvoidWhenPlanning.CLIMATE_AND_SURROUNDINGS]: [
    'extreme_heat',
    'cold_weather',
    'humidity',
    'rainy_weather',
    'high_altitude',
    'loud_places',
    'busy_roads',
  ],

  [WhatShouldAvoidWhenPlanning.FOOD_AND_DRINK]: [
    'chain_restaurants',
    'spicy_food',
    'seafood',
    'tasting_menus',
    'alcohol_led_activities',
    'late_dinners',
    'unplanned_meals',
    'street_food',
    'fine_dining',
  ],

  [WhatShouldAvoidWhenPlanning.WHERE_YOU_STAY]: [
    'hostels',
    'shared_bathrooms',
    'large_resorts',
    'remote_stays',
    'small_rooms',
    'stays_without_lifts_or_elevators',
    'party_hotels',
    'camping',
  ],

  [WhatShouldAvoidWhenPlanning.ACTIVITIES]: [
    'museums',
    'guided_tours',
    'shopping',
    'nightlife',
    'theme_parks',
    'beach_days',
    'water_activities',
    'animal_attractions',
    'wellness_activities',
    'boat_trips',
    'hiking',
    'cycling',
    'adventure_sports',
  ],

  [WhatShouldAvoidWhenPlanning.TRANSPORT]: [
    'ferries',
    'small_aircraft',
    'seaplanes',
    'driving',
    'public_transport',
    'long_transfers',
  ],

  [WhatShouldAvoidWhenPlanning.EVENTS_AND_ENTERTAINMENT]: [
    'theatre_and_shows',
    'sporting_events',
    'comedy_and_live_entertainment',
    'concerts',
    'festivals',
  ],
};

function YourDefaults({
  onComplete,
  onCompleteWhenDirty,
}: YourDefaultsProps) {
  const { t } = useTranslation();

  const { nextStep, setContextData, contextData } =
    useStepperContext<
      typeof allTravelDnaSteps,
      TravelDnaFormData
    >();

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [activeCategory, setActiveCategory] =
    useState<AvoidanceCategory | null>(
      contextData?.what_should_avoid_when_planning?.[0] ?? null
    );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<YourDefaultsSchema>({
    resolver: zodResolver(yourDefaultsSchema),
    defaultValues: {
      what_should_avoid_when_planning:
        contextData?.what_should_avoid_when_planning ?? [],
      what_should_avoid_when_planning_details:
        contextData?.what_should_avoid_when_planning_details ?? {},
      what_should_avoid_when_planning_custom:
        contextData?.what_should_avoid_when_planning_custom ?? '',
    },
  });

  const selected =
    watch('what_should_avoid_when_planning') ?? [];

  const details =
    watch('what_should_avoid_when_planning_details') ?? {};

  const toggleCategory = (category: AvoidanceCategory) => {
    setActiveCategory(category);
  };

  const toggleDetail = (
    category: AvoidanceCategory,
    detail: string
  ) => {
    const current = details[category] ?? [];

    const next = current.includes(detail)
      ? current.filter((item) => item !== detail)
      : [...current, detail];

    const nextDetails = {
      ...details,
      [category]: next,
    };

    setValue(
      'what_should_avoid_when_planning_details',
      nextDetails,
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );

    setValue(
      'what_should_avoid_when_planning',
      savedCategories(
        selected,
        nextDetails,
        visibleCategories
      ) as AvoidanceCategory[],
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const onSubmit = (data: YourDefaultsSchema) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);

        const savedAvoidance = savedCategories(
          data.what_should_avoid_when_planning ?? [],
          data.what_should_avoid_when_planning_details ?? {},
          visibleCategories
        ) as AvoidanceCategory[];

        const avoidanceChanged =
          savedAvoidance.join() !==
          (contextData?.what_should_avoid_when_planning ?? []).join();

        const cleanData: YourDefaultsSchema = {
          ...data,
          what_should_avoid_when_planning: savedAvoidance,
          what_should_avoid_when_planning_custom:
            data.what_should_avoid_when_planning_custom?.trim() ||
            null,
        };

        const context = {
          ...contextData,
          ...cleanData,
        };

        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_DEFAULTS,
          data: cleanData,
          context,
        };

        if (isDirty || avoidanceChanged) {
          setContextData((previous) => ({
            ...previous,
            ...cleanData,
          }));

          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  const activeCategoryDetails = activeCategory
    ? categoryDetails[activeCategory] ?? []
    : [];

  const selectedDetailLabels = visibleCategories.flatMap(
    (category) =>
      (details[category] ?? []).map((detail) => ({
        key: `${category}-${detail}`,
        label: t(
          `travelDnaPage.yourDefaults.details.${category}.${detail}`
        ),
      }))
  );

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourDefaults.header')}
      title={t('travelDnaPage.yourDefaults.subheader')}
      description={t(
        'travelDnaPage.yourDefaults.description'
      )}
      maxWidthClassName="max-w-[970px]"
    >
      <form
        className="mt-7 flex w-full flex-col items-center"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <DnaError error={error} className="mb-6" />

        <div className="flex flex-wrap justify-center gap-4">
          {visibleCategories.map((category) => {
            const selectedDetailCount =
              details[category]?.length ?? 0;

            return (
              <DnaPill
                key={category}
                label={t(
                  `travelDnaPage.yourDefaults.categories.${category}`
                )}
                selected={selectedDetailCount > 0}
                badgeCount={selectedDetailCount}
                disabled={isPending}
                onClick={() => toggleCategory(category)}
              />
            );
          })}
        </div>

        {activeCategory &&
        activeCategoryDetails.length > 0 ? (
          <div className="mt-12 flex flex-wrap justify-center gap-5">
            {activeCategoryDetails.map((detail) => (
              <DnaPill
                key={`${activeCategory}-${detail}`}
                label={t(
                  `travelDnaPage.yourDefaults.details.${activeCategory}.${detail}`
                )}
                selected={Boolean(
                  details[activeCategory]?.includes(detail)
                )}
                disabled={isPending}
                onClick={() =>
                  toggleDetail(activeCategory, detail)
                }
              />
            ))}
          </div>
        ) : null}

        <label className="mt-12 w-full text-left font-sans text-base text-[var(--cream)]">
          {t('travelDnaPage.yourDefaults.customLabel')}{' '}

          <span className="rounded-full border border-[var(--gold-deep)] px-2 py-1 text-[10px] uppercase text-[var(--gold)]">
            {t('travelDnaPage.yourDefaults.optional')}
          </span>

          <input
            {...register(
              'what_should_avoid_when_planning_custom'
            )}
            className="mt-3 h-12 w-full rounded-[9px] border border-[var(--gold-deep)] bg-[var(--gold-deep)] px-4 font-sans text-base font-semibold text-white outline-none placeholder:text-white/85 focus:border-[var(--gold-lt)]"
            placeholder={t(
              'travelDnaPage.yourDefaults.customPlaceholder'
            )}
          />
        </label>

        <section className="mt-6 min-h-[128px] w-full rounded-[10px] border border-[var(--gold-deep)] p-4 text-left">
          <h2 className="font-sans text-sm font-semibold uppercase text-[var(--gold)]">
            {t('travelDnaPage.yourDefaults.summaryLabel')}
          </h2>

          <div className="mt-3 flex flex-wrap gap-2">
            {selectedDetailLabels.map(({ key, label }) => (
              <span
                key={key}
                className="rounded-full bg-[var(--gold-lt)] px-4 py-2 font-sans text-sm text-[var(--ink)]"
              >
                {label}
              </span>
            ))}
          </div>
        </section>

        <DnaPrimaryButton
          className="mt-7"
          loading={isPending}
          type="submit"
        >
          {t('travelDnaPage.yourDefaults.continue')}
        </DnaPrimaryButton>
      </form>
    </DnaStepLayout>
  );
}

export default YourDefaults;